import { ApiError, makeApiRequest } from "$lib/utils/api";
import { parseStatusRecord } from "$lib/components/security/exposedSensitiveDocuments/status";
import {
  STATUS_NOTE_MAX_LENGTH,
  type SettableStatus,
  type StatusSaveOutcome,
} from "$lib/components/security/exposedSensitiveDocuments/statusSave";

/**
 * Setting a row's review status on the Exposed Sensitive Documents page (#2253), through
 * `PUT /orgs/{orgId}/exposed-sensitive-documents/{documentId}/status` (the only writer).
 */

const statusPath = (orgId: string, documentId: string) =>
  `/orgs/${orgId}/exposed-sensitive-documents/${encodeURIComponent(documentId)}/status`;

/**
 * Save a status. Never throws. The note is trimmed; an empty or whitespace-only note is left out
 * of the body, and so is any note sent with `open` (the route ignores it). A trimmed note over
 * {@link STATUS_NOTE_MAX_LENGTH} is `failed` without a request, the same answer the route gives.
 */
export async function saveExposedDocumentStatus(
  orgId: string,
  documentId: string,
  status: SettableStatus,
  note?: string,
): Promise<StatusSaveOutcome> {
  const trimmed = status === "open" ? "" : (note ?? "").trim();
  if (trimmed.length > STATUS_NOTE_MAX_LENGTH) return { kind: "failed" };
  const body = trimmed === "" ? { status } : { status, note: trimmed };

  let response: unknown;
  try {
    response = await makeApiRequest(statusPath(orgId, documentId), body, "PUT");
  } catch (error) {
    return error instanceof ApiError && error.status === 404 ? { kind: "swept" } : { kind: "failed" };
  }

  // `makeApiRequest` answers a 204 with `undefined`: the record was deleted, the row is `open`.
  if (response === undefined) return { kind: "saved", record: undefined };
  const record = parseStatusRecord(response);
  return record ? { kind: "saved", record } : { kind: "failed" };
}
